import { createGeneralProductInformationV2Validation } from './general-product-information-v2-validation'

const DEFAULT_SAVE_DELAY = 2500

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`General Product Information V2 requires services.${name}()`)
    }

    return service
}

const createGeneralProductInformationV2Controller = ({
    context,
    step = { settings: {} },
    setValidity = () => {},
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    saveDelay = DEFAULT_SAVE_DELAY,
}) => {
    let saveTimeoutId = null
    let pendingSave = null
    let hasUnsavedCardChanges = false
    let validation = createGeneralProductInformationV2Validation(context, step.settings)
    let disposed = false

    const publish = effect => Promise.resolve(publishEffect(effect)).catch(reportError)
    const refreshValidation = () => {
        validation = createGeneralProductInformationV2Validation(context, step.settings)
        setValidity(validation)
    }

    const performCardSave = () => {
        if (pendingSave) {
            return pendingSave
        }

        setSaving(true)
        pendingSave = Promise.resolve()
            .then(() => getService(context, 'saveProductCard')())
            .then(() => publishEffect({
                type: 'product.card.changed',
                phase: 'committed',
                scopes: ['product.card'],
            }))
            .then(() => {
                hasUnsavedCardChanges = false
                setDirty(false)
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                pendingSave = null
                setSaving(false)
            })

        return pendingSave
    }

    const scheduleCardSave = () => {
        if (disposed) {
            return Promise.resolve()
        }

        hasUnsavedCardChanges = true
        setDirty(true)
        publish({
            type: 'product.card.changed',
            phase: 'draft',
            scopes: ['product.card'],
        })
        clearTimeout(saveTimeoutId)
        saveTimeoutId = setTimeout(() => {
            saveTimeoutId = null
            performCardSave().catch(() => undefined)
        }, saveDelay)

        return Promise.resolve()
    }

    const runImmediateAction = async (serviceName, args, effect) => {
        setSaving(true)
        setDirty(true)

        try {
            await getService(context, serviceName)(...args)
            await publishEffect({
                ...effect,
                phase: 'committed',
            })
            setDirty(false)
            reportError(null)
        } catch (error) {
            reportError(error)
            throw error
        } finally {
            setSaving(false)
        }
    }

    return Object.freeze({
        async load() {
            if (context.mode === 'bulk') {
                refreshValidation()
                return
            }

            const [hierarchy, attributes] = await Promise.all([
                getService(context, 'getProductHierarchy')(),
                getService(context, 'getProductAttributes')(),
                getService(context, 'refreshProductWeightRequirement')()
                    .catch(() => false),
            ])

            Object.assign(context.productHierarchy, hierarchy || { children: [] })
            Object.assign(context, { productAttributes: attributes })
            refreshValidation()
        },
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        scheduleSave: scheduleCardSave,
        async handleAction(action) {
            if (disposed) {
                return
            }

            switch (action.type) {
            case 'product-type.change':
                await runImmediateAction(
                    'changeProductType',
                    [action.payload.type],
                    { type: 'product.card.changed', scopes: ['product.card'] },
                )
                return
            case 'suppliers.change':
                await runImmediateAction(
                    'changeProductSuppliers',
                    [action.payload.suppliers],
                    { type: 'product.card.changed', scopes: ['product.card'] },
                )
                return
            case 'complexity.change':
                await runImmediateAction(
                    'changeProductComplexity',
                    [action.payload.isComplex],
                    { type: 'request.details.changed', scopes: ['request.details'] },
                )
                return
            case 'characteristics.change':
                getService(context, 'setProductCharacteristics')(action.payload.characteristics)
                await scheduleCardSave()
                return
            case 'bulk-request.remove':
                setSaving(true)
                try {
                    await getService(context, 'updateBulkReplySelection')(
                        context.bulkReplyCandidates.selectedRequestIds
                            .filter(requestId => requestId !== action.payload.requestId),
                    )
                    refreshValidation()
                    await publishEffect({
                        type: 'request.bulk-reply-candidates.changed',
                        phase: 'committed',
                        scopes: ['request.bulk-reply-candidates'],
                    })
                    reportError(null)
                } catch (error) {
                    reportError(error)
                    throw error
                } finally {
                    setSaving(false)
                }
                return
            default:
                throw new Error(`Unsupported General Product Information V2 action "${action.type}"`)
            }
        },
        async flush() {
            clearTimeout(saveTimeoutId)
            saveTimeoutId = null

            if (pendingSave) {
                await pendingSave
            }

            if (hasUnsavedCardChanges) {
                await performCardSave()
            }
        },
        async handleEffect(effect) {
            if (effect.type === 'product.concern-substances.changed') {
                await getService(context, 'refreshProductWeightRequirement')()
            }
        },
        dispose() {
            disposed = true
            clearTimeout(saveTimeoutId)
            saveTimeoutId = null
        },
    })
}

export {
    DEFAULT_SAVE_DELAY,
    createGeneralProductInformationV2Controller,
}
export default createGeneralProductInformationV2Controller
